import { createContext, useContext } from "react";
import type * as React from "react";

import { Pin } from "./Pin";
import { cn } from "../lib/cn";

/*
 * The cork material.
 *
 * Cork tan with a quiet ink speckle: two offset dot lattices at coprime periods
 * (11px and 7px) so it reads as grain rather than polka dots, held to 4-5% ink.
 * Texture lives on desk and cork backgrounds only (DESIGN.md rule 5) - this is
 * the cork half of that exception, which is why the recipe is a component and
 * not a utility anyone can paint onto a panel.
 *
 * Both speckle layers mix from `--color-ink`, so nothing here is an invented
 * color; only the lattice geometry is literal, and geometry is not palette.
 */
const CORK_MATERIAL = cn(
  "bg-cork",
  "[background-image:radial-gradient(color-mix(in_srgb,var(--color-ink)_5%,transparent)_1px,transparent_1px),radial-gradient(color-mix(in_srgb,var(--color-ink)_4%,transparent)_1px,transparent_1px)]",
  "[background-size:11px_11px,7px_7px]",
  "[background-position:0_0,4px_3px]",
);

/**
 * How the board sits in the room. Both keep the edge light; only the drop
 * shadow differs.
 */
export type CorkboardElevation = "float" | "flush";

/*
 * Edge light and the drop shadow share one box-shadow so two shadow utilities
 * can't fight over the property (the Clipboard trick). That also means a caller
 * who passes their own `shadow-*` replaces the edge light along with the lift -
 * intended: the board is then something other than a board resting on the desk.
 *
 * Both entries repeat the inset pair in full rather than composing it from a
 * constant: Tailwind finds classes by scanning source text, so anything built
 * at runtime emits no CSS (the Tabs MANILA rule). rgba() rather than tokens for
 * the same reason it appears in Pin and LabelTape - this is light on an object,
 * not palette.
 */
const elevationShadow: Record<CorkboardElevation, string> = {
  float:
    "shadow-[inset_0_1px_0_rgba(255,255,255,0.22),inset_0_-1px_3px_rgba(0,0,0,0.1),var(--shadow-float)]",
  flush:
    "shadow-[inset_0_1px_0_rgba(255,255,255,0.22),inset_0_-1px_3px_rgba(0,0,0,0.1)]",
};

/**
 * True inside a `Corkboard`. Read only by `PinnedPaper`, which refuses to
 * render anywhere else.
 */
const CorkboardContext = createContext(false);

export interface CorkboardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * `float` (default) is the board held over the desk; `flush` drops the lift
   * for a board set into a surface that already has depth of its own.
   */
  elevation?: CorkboardElevation;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * A corkboard: the one surface in the system that things get pinned to. Cork
 * tan under a fine ink speckle, lit along the top edge and floating over the
 * desk.
 *
 * The board owns the material and nothing else. Layout stays with the caller -
 * the board is a surface, not a grid, so padding, scroll region, radius and any
 * grid inside it come through `className`, exactly as they would on a bare div.
 *
 * Everything pinned to it goes in a `PinnedPaper`, which is the only way to get
 * a `Pin` onto a card without placing one by hand. `PinnedPaper` throws outside
 * a board, so "pins belong on cork" is enforced by the type of surface you are
 * standing on rather than by review: to pin something you must first have a
 * board to pin it to. Objects that merely lie on the board (a folder, a reader
 * pane) are ordinary children and take no pin.
 */
export function Corkboard({
  elevation = "float",
  className,
  children,
  ...rest
}: CorkboardProps) {
  return (
    <CorkboardContext.Provider value={true}>
      <div
        className={cn(CORK_MATERIAL, elevationShadow[elevation], className)}
        {...rest}
      >
        {children}
      </div>
    </CorkboardContext.Provider>
  );
}

export interface PinnedPaperOwnProps {
  /**
   * The element to render. Defaults to `div`; pass `as="button"` (or `p`, `li`,
   * `article`) so the pinned object *is* the interactive element rather than
   * sitting inside one - a wrapper would leave the pin behind when the card
   * lifts on hover.
   */
  as?: React.ElementType;
  /** Moves the pin off top-center - a corner, an inset. See `Pin`. */
  pinClassName?: string;
  className?: string;
  children?: React.ReactNode;
}

/**
 * Props for `PinnedPaper`. Generic over the rendered element, so `as="button"`
 * brings `type`, `disabled` and the button event handlers with it.
 */
export type PinnedPaperProps<T extends React.ElementType = "div"> =
  PinnedPaperOwnProps & { as?: T } & Omit<
      React.ComponentPropsWithRef<T>,
      keyof PinnedPaperOwnProps
    >;

/**
 * A sheet of card stock pinned to the board: flat paper, a pushpin through the
 * top, and the small shadow of something resting a millimetre off the cork.
 *
 * It owns the pin and the paper the pin goes through - `relative` (the pin's
 * anchor) and `rounded-paper bg-card shadow-lift`. Its edge treatment, padding,
 * type and any interactive chrome are the caller's, through `className`, so a
 * room tile and a scrap of note paper are the same object dressed differently.
 * Pinned paper hangs straight: a tilt would land on the tiles and notes people
 * read off the board.
 *
 * It renders only inside a `Corkboard` and throws otherwise. That is the whole
 * enforcement of "pins belong on cork": a pin through a sheet lying on a desk
 * or a page is a pin through the desk, and the way not to do that is to make
 * the pin unreachable without a board. Use the raw `Pin` only when you are
 * building another board-like surface.
 */
export function PinnedPaper<T extends React.ElementType = "div">({
  as,
  pinClassName,
  className,
  children,
  ...rest
}: PinnedPaperProps<T>) {
  const onCork = useContext(CorkboardContext);
  if (!onCork) {
    throw new Error(
      "<PinnedPaper> has to be rendered inside <Corkboard>. Pins belong on cork.",
    );
  }

  const Element = (as ?? "div") as React.ElementType;

  return (
    <Element
      className={cn("relative rounded-paper bg-card shadow-lift", className)}
      {...rest}
    >
      <Pin className={pinClassName} />
      {children}
    </Element>
  );
}
